import { Dispatch, SetStateAction, useMemo } from 'react';
import * as R from 'remeda';
import { IntlShape, useIntl } from 'react-intl';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { EmptyStateRadar } from '@rossum/ui';
import {
  IconCalendarMonth,
  IconChevronDown,
  IconChevronUp,
} from '@rossum/ui/icons/tabler';
import {
  Stack,
  SvgIcon,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@rossum/ui/material';
import { DataGridPro, gridClasses } from '@rossum/ui/x-data-grid-pro';
import { resolveIconType } from '../../../formulas/FormulaPreview/hooks/utils';
import { SchemaFieldWithPathNonNullableField } from '../../../schemas/getFlattenSchemaFields';
import {
  convertErrorRateToPrecisionTarget,
  EMPTY_PLACEHOLDER,
  getRowId,
} from '../core/utils';
import { RenderDetailPanel } from './components';
import { AutomationCharts } from './components/AutomationCharts';
import { useAutomationInsightsColumns } from './hooks/useAutomationInsightsColumns';
import {
  AggregationPeriod,
  AutomationInsightssRowModel,
  TransformedAutomationInsights,
} from './types';
import {
  getBlockerTranslation,
  getDetailPanelHeight,
  getPeriodBoundaries,
} from './utils';

const EMPTY_GRID_HEIGHT = 600;

type AutomationInsightsProps = {
  aggregationPeriod: AggregationPeriod;
  setAggregationPeriod: Dispatch<SetStateAction<AggregationPeriod>>;
  isLoading: boolean;
  isLoadingAutomationInsights: boolean;
  transformedAutomationData: TransformedAutomationInsights | null;
  schemaMap: Record<string, SchemaFieldWithPathNonNullableField>;
  queueId: number;
  qualitySpotCheckEnabled: boolean;
};

const stringifyDatePeriod = (
  dates: { startDate: string | null; endDate: string | null },
  intl: IntlShape
) =>
  dates.startDate && dates.endDate
    ? `${intl.formatDate(new Date(dates.startDate), { month: 'short', day: 'numeric' })} - ${intl.formatDate(new Date(dates.endDate), { month: 'short', day: 'numeric' })}`
    : '';

export const AutomationInsights = ({
  aggregationPeriod,
  setAggregationPeriod,
  isLoading,
  transformedAutomationData,
  schemaMap,
  isLoadingAutomationInsights,
  queueId,
  qualitySpotCheckEnabled,
}: AutomationInsightsProps) => {
  const intl = useIntl();

  const dates = useMemo(() => {
    return getPeriodBoundaries(
      transformedAutomationData?.documentAutomationTimeseries ?? []
    );
  }, [transformedAutomationData]);

  const columns = useAutomationInsightsColumns({
    hasRoughPrecision: Boolean(
      transformedAutomationData?.datapointStatistics.some(
        dp => !dp.isQualityEstimate
      )
    ),
  });

  const rows = useMemo<AutomationInsightssRowModel[]>(() => {
    if (!transformedAutomationData) return [];

    // Annotation level blockers are somewhat an exception in this context.
    // Insights table reports blocked documents and their reasons. A document can be blocked by mishap in a datapoint field in an annotation, or it can be blocked by a reason unrelated to a datapoint;
    // in this case related to the annotation itself.
    // To show all types of blockers in the insights table, we concatanate annotation level blockers to the end of the table
    const annotationLevelBlockers: AutomationInsightssRowModel[] = R.pipe(
      transformedAutomationData.documentBlockers,
      R.filter(blocker => blocker.granularity === 'annotation'),
      R.map(
        ({ blocker, documentCount, ...rest }) =>
          ({
            type: 'annotation-level-blocker',
            label: getBlockerTranslation({ intl, key: blocker }),
            isDeleted: false,
            // deprecated
            blockedDocumentCounts: {},
            blockers: [{ blocker, documentCount, ...rest }],
            schemaId: blocker,
            // unrelated to annotation level blockers
            confidenceThreshold: null,
            estimatedErrorRate: null,
            isQualityEstimate: false,
            precision: null,
            queueId,
          }) as const satisfies AutomationInsightssRowModel
      )
    );

    const datapointStatistics: AutomationInsightssRowModel[] = R.pipe(
      transformedAutomationData.datapointStatistics,
      R.map(item => {
        const { field } = schemaMap[item.schemaId] ?? {};

        const type = field ? resolveIconType(field) : null;

        return {
          ...item,
          label: field?.label ?? EMPTY_PLACEHOLDER,
          isDeleted: !field,
          type,
          queueId,
          precision: R.isNumber(item.estimatedErrorRate)
            ? convertErrorRateToPrecisionTarget(item.estimatedErrorRate)
            : null,
        } as const satisfies AutomationInsightssRowModel;
      })
    );

    return datapointStatistics.concat(annotationLevelBlockers);
  }, [schemaMap, intl, transformedAutomationData, queueId]);

  return (
    <>
      {!isLoadingAutomationInsights && transformedAutomationData ? (
        <Stack
          direction="row"
          sx={{ justifyContent: 'flex-end' }}
          spacing={1}
          useFlexGap
        >
          <TextField
            value={stringifyDatePeriod(dates, intl)}
            disabled
            size="small"
            slotProps={{
              htmlInput: { sx: { pl: 1 } },
              input: {
                startAdornment: (
                  <SvgIcon fontSize="medium">
                    <IconCalendarMonth />
                  </SvgIcon>
                ),
              },
            }}
          />
          <ToggleButtonGroup
            exclusive
            value={aggregationPeriod}
            onChange={(_, value) => {
              if (value === 'weeks' || value === 'months' || value === 'days') {
                setAggregationPeriod(value);
              }
            }}
          >
            <ToggleButton value="days">D</ToggleButton>
            <ToggleButton value="weeks">W</ToggleButton>
            <ToggleButton value="months">M</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      ) : null}

      {!isLoadingAutomationInsights && transformedAutomationData ? (
        <AutomationCharts
          data={transformedAutomationData}
          aggregationPeriod={aggregationPeriod}
          queueId={queueId}
          qualitySpotCheckEnabled={qualitySpotCheckEnabled}
        />
      ) : null}

      <ContentWrapper>
        <Stack sx={{ minHeight: EMPTY_GRID_HEIGHT }}>
          <DataGridPro
            disableRowSelectionExcludeModel
            loading={isLoading}
            getRowId={getRowId}
            rows={rows}
            columns={columns}
            disableColumnPinning
            disableColumnReorder
            disableRowSelectionOnClick
            disableColumnSelector
            slotProps={{
              loadingOverlay: {
                sx: { height: isLoading ? EMPTY_GRID_HEIGHT : 'auto' },
              },
            }}
            slots={{
              noRowsOverlay: props => (
                <Stack sx={{ height: EMPTY_GRID_HEIGHT }} {...props}>
                  <EmptyStateRadar
                    title={intl.formatMessage({
                      id: 'containers.settings.queues.queue.automation.table.emptyState.title',
                    })}
                  />
                </Stack>
              ),
              detailPanelCollapseIcon: () => (
                <SvgIcon fontSize="medium">
                  <IconChevronUp />
                </SvgIcon>
              ),
              detailPanelExpandIcon: () => (
                <SvgIcon fontSize="medium">
                  <IconChevronDown />
                </SvgIcon>
              ),
              footer: () => null,
            }}
            getDetailPanelContent={params =>
              params.row.type === 'annotation-level-blocker' ? undefined : (
                <RenderDetailPanel
                  blockers={params.row.blockers}
                  columns={params.columns}
                  queueId={queueId}
                />
              )
            }
            getDetailPanelHeight={getDetailPanelHeight}
            sx={{
              ...commonDataGridStyles,
              '&': {
                '--DataGrid-pinnedBackground': t => t.palette.background.paper,
                '--DataGrid-containerBackground': t =>
                  t.palette.background.paper,
              },
              // the default height 100% in virtualScroller does not take effect when DataGrid does not have a fixed height.
              [`& .${gridClasses.virtualScroller}`]: { flexGrow: 1 },
            }}
          />
        </Stack>
      </ContentWrapper>
    </>
  );
};
